import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook for Google Analytics 4 integration
 * Handles dynamic script loading and page view tracking
 */
export function useGoogleAnalytics() {
  const location = useLocation();

  useEffect(() => {
    // Early return if not in production
    if (import.meta.env?.MODE !== 'production') {
      console.log('Google Analytics disabled in development mode');
      return;
    }

    // Check if measurement ID is available
    const measurementId = import.meta.env?.VITE_GOOGLE_ANALYTICS_ID || import.meta.env?.VITE_GA_MEASUREMENT_ID;
    if (!measurementId || measurementId === 'your-google-analytics-id-here') {
      console.warn('GA Measurement ID not found or not configured in environment variables');
      return;
    }

    // Initialize gtag.js if not already done
    if (!window?.dataLayer) {
      try {
        // Load gtag.js script dynamically
        const script = document.createElement('script');
        script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
        script.async = true;
        script.onerror = () => {
          console.warn('Failed to load Google Analytics script');
        };
        document.head?.appendChild(script);

        // Initialize dataLayer and gtag function
        window.dataLayer = window.dataLayer || [];
        function gtag(...args) {
          window.dataLayer?.push(args);
        }
        window.gtag = gtag;
        
        // Configure GA
        gtag('js', new Date());
        gtag('config', measurementId, {
          page_title: document?.title || '',
          page_location: window?.location?.href || '',
          send_page_view: true
        });
      } catch (error) {
        console.warn('Error initializing Google Analytics:', error);
      }
    }

    // Send page_view event on route changes
    try {
      if (window?.gtag && typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_path: (location?.pathname || '') + (location?.search || ''),
          page_title: document?.title || '',
          page_location: window?.location?.href || '',
        });
      }
    } catch (error) {
      console.warn('Error tracking page view:', error);
    }
  }, [location]);
}

/**
 * Track custom events
 * @param {string} action - Event action
 * @param {string} category - Event category
 * @param {string} label - Event label (optional)
 * @param {number} value - Event value (optional)
 */
export function trackEvent(action, category, label = null, value = null) {
  try {
    if (import.meta.env?.MODE === 'production' && window?.gtag && typeof window.gtag === 'function') {
      const eventParams = {
        event_category: category,
      };

      // Add optional parameters if provided
      if (label !== null) eventParams.event_label = label;
      if (value !== null && !isNaN(value)) eventParams.value = Number(value);

      window.gtag('event', action, eventParams);
    }
  } catch (error) {
    console.warn('Error tracking event:', error);
  }
}

/**
 * Track form submissions
 * @param {string} formName - Name of the form
 * @param {string} formType - Type of form (contact, booking, etc.)
 */
export function trackFormSubmission(formName, formType = 'form') {
  trackEvent('form_submit', formType, formName);
}

/**
 * Track button clicks
 * @param {string} buttonName - Name/ID of the button
 * @param {string} section - Section where button is located
 */
export function trackButtonClick(buttonName, section = 'general') {
  trackEvent('button_click', section, buttonName);
}

/**
 * Track service interactions
 * @param {string} serviceType - Type of service
 * @param {string} action - Action taken (view, select, book)
 */
export function trackServiceInteraction(serviceType, action = 'view') {
  trackEvent(action, 'service', serviceType);
}

/**
 * Track search actions
 * @param {string} searchTerm - Search query
 * @param {string} searchType - Type of search (services, products, etc.)
 */
export function trackSearch(searchTerm, searchType = 'general') {
  try {
    if (import.meta.env?.MODE === 'production' && window?.gtag && typeof window.gtag === 'function') {
      window.gtag('event', 'search', {
        search_term: String(searchTerm || ''),
        search_type: String(searchType || 'general'),
      });
    }
  } catch (error) {
    console.warn('Error tracking search:', error);
  }
}

/**
 * Track e-commerce events
 * @param {string} action - Action (view_item, add_to_cart, purchase)
 * @param {Object} item - Item details
 */
export function trackEcommerce(action, item) {
  try {
    if (import.meta.env?.MODE === 'production' && window?.gtag && typeof window.gtag === 'function') {
      const eventData = {
        currency: 'USD',
        value: Number(item?.price || 0),
        items: [item]
      };
      
      window.gtag('event', action, eventData);
    }
  } catch (error) {
    console.warn('Error tracking e-commerce event:', error);
  }
}